import type { PhraseVerdict } from '@tunasong/music-lib'
import type { PickerItem } from './picker-items.js'
import type { Singable } from './practice-target.js'

/**
 * "Sing this section" (Pitch Trainer Phase 2, plan M6 step 6.7; Joni §6): the section's phrases in order,
 * each with its feedback, then a summary. Pure: which steps a section runs, and every string the run shows
 * that is not lexicon copy.
 */

/** A melody run counts phrases; chord-only material counts parts, never phrases (Joni §9) */
export type RunKind = 'phrase' | 'part'

export interface RunStep {
  singable: Singable
  /** The picker row's label, for the summary: "Chorus · bars 9–12", "Guide tones · Chorus, line 2" */
  label: string
}

export interface SectionRun {
  kind: RunKind
  steps: RunStep[]
  /** Why the run sings what it does, when that is not the default: the outline fallback (Bowie, M6) */
  note?: string
}

/** Bowie, M6; new copy, with the user via Brian */
const outlineFallbackNote =
  'The guide-tone line stays on one note in this section, so this run sings the chord outlines.'

/** How a step's own take went. A step that was never sung whole (refused, or skipped) has no outcome */
export type RunOutcome =
  | { kind: 'scored'; headline: PhraseVerdict | null; scorePercent: number }
  /** Nothing passed the voicing gate: Phase 1's diagnosis line, never a verdict (pitfall 9) */
  | { kind: 'not-heard'; lexiconId: string }

const toStep = (item: PickerItem): RunStep => ({
  singable:
    item.kind === 'phrase'
      ? { kind: 'phrase', source: item.source, phrase: item.phrase }
      : { kind: item.kind, source: item.source, part: item.part },
  label: item.label,
})

/**
 * The steps for one section's picker rows: its melody phrases when it has a melody; otherwise its
 * guide-tone parts, never the outline (plan 6.7). One-note guide-tone parts are never offered (#2455), so
 * the rest run without them. Only when EVERY guide-tone part is one note does the section offer outlines
 * only (Joni, #2458), and then the run sings those, saying why (Bowie, M6). Never a mix of the two. Null
 * when there is nothing to sing.
 */
export const sectionRun = (items: PickerItem[]): SectionRun | null => {
  const phrases = items.filter(item => item.kind === 'phrase')
  if (phrases.length > 0) {
    return { kind: 'phrase', steps: phrases.map(toStep) }
  }
  const guideTones = items.filter(item => item.kind === 'guide-tones')
  if (guideTones.length > 0) {
    return { kind: 'part', steps: guideTones.map(toStep) }
  }
  const outlines = items.filter(item => item.kind === 'outline' && item.isOutlineOnly)
  return outlines.length > 0 ? { kind: 'part', steps: outlines.map(toStep), note: outlineFallbackNote } : null
}

/** Whether a singable is this step's own material, and not one of its halves */
export const isRunStep = (step: RunStep, singable: Singable) => {
  const own = step.singable
  if (own.kind === 'phrase' || singable.kind === 'phrase') {
    return own.kind === 'phrase' && singable.kind === 'phrase' && own.phrase === singable.phrase
  }
  return own.kind === singable.kind && own.part === singable.part
}

/** "phrase 2 of 4", "part 2 of 4" */
export const runPosition = (kind: RunKind, index: number, total: number) => `${kind} ${index + 1} of ${total}`

/** Joni §6: no auto-advance; the singer moves on */
export const runNextLabel = (kind: RunKind) => (kind === 'phrase' ? 'Next phrase' : 'Next part')

export const isOnPitchOutcome = (outcome: RunOutcome | undefined) =>
  outcome?.kind === 'scored' && outcome.headline?.id === 'vocal-phrase-on-pitch'

/** "3 of 4 phrases on pitch", by §7's strict gate: the on-pitch verdict, and nothing else */
export const runSummaryHeadline = (kind: RunKind, outcomes: (RunOutcome | undefined)[]) => {
  const count = outcomes.filter(isOnPitchOutcome).length
  const noun = outcomes.length === 1 ? kind : `${kind}s`
  return `${count} of ${outcomes.length} ${noun} on pitch`
}
